<div class="page-container">
  <app-page-header
    title="Audio Upload / Recording"
    subtitle="Upload an MP3 or capture a new recording for station approval"
    [showBackButton]="true"
    backRoute="/recordings"
    backLabel="Back to Recordings">
  </app-page-header>

  <div class="content-wrapper">
    <mat-tab-group class="audio-tabs" animationDuration="200ms">

      <!-- UPLOAD TAB -->
      <mat-tab>
        <ng-template mat-tab-label>
          <mat-icon aria-hidden="true">cloud_upload</mat-icon>
          <span>Upload</span>
        </ng-template>

        <section class="panel">
          <p class="panel-intro">Upload an MP3 file or switch to the Record tab to capture new audio.</p>

          <div class="form-block">
            <header class="form-block__head">
              <span class="form-block__icon">
                <mat-icon aria-hidden="true">upload_file</mat-icon>
              </span>
              <div>
                <h3 class="form-block__title">Audio file</h3>
                <p class="form-block__subtitle">MP3 only — drag and drop or browse</p>
              </div>
            </header>

            <div
              class="uploader"
              [class.uploader--has-file]="!!uploadAudio"
              (drop)="onUploadDrop($event)"
              (dragover)="onUploadDragOver($event)"
              tabindex="0"
              role="button"
              aria-label="Drag and drop MP3 file here">
              <mat-icon aria-hidden="true">cloud_upload</mat-icon>
              <p>Drag &amp; drop your MP3 here, or</p>
              <button type="button" class="btn-secondary btn-sm" (click)="$event.preventDefault(); uploadFileInput.click()">
                <mat-icon aria-hidden="true">folder_open</mat-icon>
                Choose File
              </button>
              <input #uploadFileInput type="file" [accept]="accept" (change)="onUploadFileInputChange($event)" hidden />
              @if (uploadAudio) {
                <p class="uploader-file-name">{{ uploadAudio.name }}</p>
              }
              @if (uploadFileError) {
                <p class="error" role="alert">{{ uploadFileError }}</p>
              }
            </div>

            @if (uploadAudio && uploadAudioUrl) {
              <div class="preview">
                <h3>Preview</h3>
                <audio #uploadAudioPlayback controls preload="metadata">
                  <source [src]="uploadAudioUrl" type="audio/mpeg">
                  Your browser does not support the audio element.
                </audio>
              </div>
            }
          </div>

          <div class="form-block">
            <header class="form-block__head">
              <span class="form-block__icon">
                <mat-icon aria-hidden="true">edit_note</mat-icon>
              </span>
              <div>
                <h3 class="form-block__title">Details</h3>
                <p class="form-block__subtitle">Name and voice actor for this upload</p>
              </div>
            </header>

            <div class="meta">
              <mat-form-field appearance="outline">
                <mat-label>Audio Name</mat-label>
                <input
                  matInput
                  id="upload-audio-name"
                  [(ngModel)]="uploadAudioName"
                  (blur)="normalizeUploadAudioName()"
                  placeholder="e.g., Spring Sale 30s" />
              </mat-form-field>
            </div>

            <div class="talent">
              <h4>Voice Actor</h4>
              @if (!uploadSelectedTalent) {
                <mat-form-field appearance="outline" class="talent-search">
                  <mat-label>Search or add voice actor</mat-label>
                  <input
                    matInput
                    [(ngModel)]="uploadTalentInput"
                    (input)="onUploadTalentInputChange(uploadTalentInput)"
                    [matAutocomplete]="uploadAuto"
                    placeholder="Type to search or create new talent" />
                  <mat-autocomplete
                    #uploadAuto="matAutocomplete"
                    [displayWith]="displayTalentName"
                    (optionSelected)="onUploadTalentSelectionChange($event.option.value)">
                    @for (t of (filteredUploadTalents$ | async); track t.id) {
                      <mat-option [value]="t">{{ t.name }}</mat-option>
                    }
                  </mat-autocomplete>
                  <mat-icon matSuffix>search</mat-icon>
                </mat-form-field>
              }

              @if (uploadSelectedTalent) {
                <div class="selected-talent">
                  <span class="selected-label"><mat-icon>check_circle</mat-icon> Selected voice actor</span>
                  <div class="station-chip">
                    <mat-icon>person</mat-icon>
                    <span>{{ uploadSelectedTalent.name }}</span>
                    <button
                      type="button"
                      class="chip-remove"
                      (click)="removeUploadSelectedTalent()"
                      aria-label="Remove voice actor">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                </div>
              } @else if (uploadTalentInput.trim() && !isUploadTalentExisting()) {
                <button type="button" class="btn-secondary btn-sm" (click)="createUploadTalent()">
                  <mat-icon aria-hidden="true">person_add</mat-icon>
                  Add "{{ uploadTalentInput }}"
                </button>
              }
            </div>
          </div>

          <div class="form-block">
            <header class="form-block__head">
              <span class="form-block__icon">
                <mat-icon aria-hidden="true">radio</mat-icon>
              </span>
              <div>
                <h3 class="form-block__title">Distribution</h3>
                <p class="form-block__subtitle">Where this recording will be available</p>
              </div>
            </header>
            <ng-container *ngTemplateOutlet="uploadDistributionFields"></ng-container>
          </div>

          @if (canAssignProductTypes) {
            <div class="form-block">
              <header class="form-block__head">
                <span class="form-block__icon">
                  <mat-icon aria-hidden="true">category</mat-icon>
                </span>
                <div>
                  <h3 class="form-block__title">Product Types</h3>
                  <p class="form-block__subtitle">Search and assign product types</p>
                </div>
              </header>
              <app-product-type-selector
                (selectionChange)="selectedProductTypeIds = $event">
              </app-product-type-selector>
            </div>
          }

          @if (canSetPreemptiblePromo) {
            <mat-checkbox [(ngModel)]="isPreemptiblePromo" class="preemptible-checkbox">
              Preemptible Promo
            </mat-checkbox>
          }

          <div class="actions">
            <button
              type="button"
              class="btn-primary btn-lg"
              (click)="submitUpload()"
              [disabled]="uploading || isUploadingFile || !uploadAudio || !uploadSelectedTalent || uploadSelectedStations.length === 0">
              <mat-icon aria-hidden="true">cloud_upload</mat-icon>
              @if (isUploadingFile) { Uploading File... } @else { Submit }
            </button>
            @if (uploading) {
              <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            }
          </div>
        </section>
      </mat-tab>

      <!-- RECORDING TAB -->
      <mat-tab>
        <ng-template mat-tab-label>
          <mat-icon aria-hidden="true">mic</mat-icon>
          <span>Record</span>
        </ng-template>

        <section class="panel">
          <div class="form-block">
            <header class="form-block__head">
              <span class="form-block__icon">
                <mat-icon aria-hidden="true">fiber_manual_record</mat-icon>
              </span>
              <div>
                <h3 class="form-block__title">Capture</h3>
                <p class="form-block__subtitle">Record up to 60 seconds of audio in the browser</p>
              </div>
            </header>

            <div class="record-controls">
              @if (isRecording) {
                <button type="button" class="btn-danger" (click)="stopRecording()">
                  <mat-icon aria-hidden="true">stop</mat-icon> Stop
                </button>
              } @else if (showPlayer) {
                <button type="button" class="btn-primary" (click)="reRecord()">
                  <mat-icon aria-hidden="true">fiber_manual_record</mat-icon> Re-record
                </button>
              } @else {
                <button type="button" class="btn-primary" (click)="startRecording()">
                  <mat-icon aria-hidden="true">fiber_manual_record</mat-icon> Start Recording
                </button>
              }
              <div class="timer" aria-live="polite">{{ timerDisplay }}</div>
            </div>

            @if (showPlayer) {
              <div class="preview compact">
                <h3>Preview</h3>
                @if (recordingAudioUrl) {
                  <audio
                    #recordingAudioPlayback
                    controls
                    preload="metadata"
                    (loadedmetadata)="onRecordingPreviewMetadataLoaded()">
                    <source [src]="recordingAudioUrl" type="audio/mpeg">
                    Your browser does not support the audio element.
                  </audio>
                }
              </div>
            }

            @if (isRecording) {
              <canvas #waveformCanvas width="600" height="60" aria-hidden="true"></canvas>
            }
          </div>

          <div class="form-block">
            <header class="form-block__head">
              <span class="form-block__icon">
                <mat-icon aria-hidden="true">edit_note</mat-icon>
              </span>
              <div>
                <h3 class="form-block__title">Details</h3>
                <p class="form-block__subtitle">Name and voice actor for this recording</p>
              </div>
            </header>

            <div class="meta">
              <mat-form-field appearance="outline">
                <mat-label>Recording Name</mat-label>
                <input
                  matInput
                  id="recording-audio-name"
                  [(ngModel)]="recordingAudioName"
                  (blur)="normalizeRecordingAudioName()"
                  placeholder="e.g., Spring Sale 30s" />
              </mat-form-field>
            </div>

            <div class="talent">
              <h4>Voice Actor</h4>
              @if (!recordingSelectedTalent) {
                <mat-form-field appearance="outline" class="talent-search">
                  <mat-label>Search or add voice actor</mat-label>
                  <input
                    matInput
                    [(ngModel)]="recordingTalentInput"
                    (input)="onRecordingTalentInputChange(recordingTalentInput)"
                    [matAutocomplete]="recordingAuto"
                    placeholder="Type to search or create new talent" />
                  <mat-autocomplete
                    #recordingAuto="matAutocomplete"
                    [displayWith]="displayTalentName"
                    (optionSelected)="onRecordingTalentSelectionChange($event.option.value)">
                    @for (t of (filteredRecordingTalents$ | async); track t.id) {
                      <mat-option [value]="t">{{ t.name }}</mat-option>
                    }
                  </mat-autocomplete>
                  <mat-icon matSuffix>search</mat-icon>
                </mat-form-field>
              }

              @if (recordingSelectedTalent) {
                <div class="selected-talent">
                  <span class="selected-label"><mat-icon>check_circle</mat-icon> Selected voice actor</span>
                  <div class="station-chip">
                    <mat-icon>person</mat-icon>
                    <span>{{ recordingSelectedTalent.name }}</span>
                    <button
                      type="button"
                      class="chip-remove"
                      (click)="removeRecordingSelectedTalent()"
                      aria-label="Remove voice actor">
                      <mat-icon>close</mat-icon>
                    </button>
                  </div>
                </div>
              } @else if (recordingTalentInput.trim() && !isRecordingTalentExisting()) {
                <button type="button" class="btn-secondary btn-sm" (click)="createRecordingTalent()">
                  <mat-icon aria-hidden="true">person_add</mat-icon>
                  Add "{{ recordingTalentInput }}"
                </button>
              }
            </div>
          </div>

          <div class="form-block">
            <header class="form-block__head">
              <span class="form-block__icon">
                <mat-icon aria-hidden="true">radio</mat-icon>
              </span>
              <div>
                <h3 class="form-block__title">Distribution</h3>
                <p class="form-block__subtitle">Where this recording will be available</p>
              </div>
            </header>
            <ng-container *ngTemplateOutlet="recordingDistributionFields"></ng-container>
          </div>

          @if (canAssignProductTypes) {
            <div class="form-block">
              <header class="form-block__head">
                <span class="form-block__icon">
                  <mat-icon aria-hidden="true">category</mat-icon>
                </span>
                <div>
                  <h3 class="form-block__title">Product Types</h3>
                  <p class="form-block__subtitle">Search and assign product types</p>
                </div>
              </header>
              <app-product-type-selector
                (selectionChange)="selectedProductTypeIds = $event">
              </app-product-type-selector>
            </div>
          }

          @if (canSetPreemptiblePromo) {
            <mat-checkbox [(ngModel)]="recordingIsPreemptiblePromo" class="preemptible-checkbox">
              Preemptible Promo
            </mat-checkbox>
          }

          <div class="actions">
            <button
              type="button"
              class="btn-primary btn-lg"
              (click)="submitRecording()"
              [disabled]="uploading || !recordingAudio || !recordingSelectedTalent || recordingSelectedStations.length === 0">
              <mat-icon aria-hidden="true">cloud_upload</mat-icon> Submit
            </button>
            @if (uploading) {
              <mat-progress-bar mode="indeterminate"></mat-progress-bar>
            }
          </div>
        </section>
      </mat-tab>
    </mat-tab-group>
  </div>
</div>

<!-- Upload tab: coverage cities + station selection -->
<ng-template #uploadDistributionFields>
  <div class="distribution-fields">
    @if (usesCoverageCityFlow) {
      <p class="coverage-helper" role="note">
        <mat-icon aria-hidden="true">info_outline</mat-icon>
        <span>These are the list of cities that currently have active stations. If the city you're looking for is not listed, please select the nearest city and station instead.</span>
      </p>

      <app-coverage-city-select
        [multiple]="true"
        [required]="true"
        [stationCoverageOnly]="true"
        label="Coverage Cities"
        placeholder="Search and select cities"
        [(ngModel)]="selectedCoverageCities"
        (citiesChange)="onCoverageCitiesChange($event)">
      </app-coverage-city-select>

      @if (selectedCoverageCities.length > 0) {
        @if (loadingStations) {
          <div class="inline-loading" role="status">
            <mat-spinner diameter="22"></mat-spinner>
            <span>Loading stations for selected cities…</span>
          </div>
        } @else if (stationsLoadError) {
          <div class="inline-error" role="alert">{{ stationsLoadError }}</div>
        } @else if (availableStations.length === 0) {
          <div class="inline-empty" role="status">No stations found for selected cities.</div>
        } @else {
          <app-station-multiselect-field
            [stations]="availableStations"
            [(selectedStations)]="uploadSelectedStations"
            label="Stations"
            hint="Choose which stations this recording should be submitted to for approval."
            [required]="true" />
        }
      }
    } @else {
      <div class="stations">
        <h4>Select Stations</h4>
        <p class="muted">Choose which stations this recording will be available on</p>

        <div class="multiselect-container">
          <div class="multiselect-input-wrapper">
            <input
              type="text"
              class="multiselect-input"
              placeholder="Search and select stations..."
              [(ngModel)]="uploadStationSearchInput"
              (input)="filterUploadStations(uploadStationSearchInput)"
              (focus)="uploadStationDropdownOpen = true"
              (blur)="onUploadStationBlur()">
            <mat-icon class="multiselect-icon">expand_more</mat-icon>
          </div>

          @if (uploadStationDropdownOpen) {
            <div
              class="multiselect-dropdown"
              (mouseenter)="uploadDropdownHover=true"
              (mouseleave)="uploadDropdownHover=false">
              @if (filteredUploadStations.length > 0) {
                @for (station of filteredUploadStations; track station.id) {
                  <div class="multiselect-option" (click)="toggleUploadStation(station)">
                    <input
                      type="checkbox"
                      [checked]="isStationSelected(uploadSelectedStations, station)"
                      (click)="$event.stopPropagation()"
                      (change)="onUploadCheckboxChange(station, $event)">
                    <span class="option-text">
                      <span class="option-name">{{ station.name }}</span>
                      <span class="option-call-sign">{{ station.call_sign }}</span>
                    </span>
                  </div>
                }
              } @else {
                <div class="multiselect-no-results">No stations found</div>
              }
            </div>
          }
        </div>

        @if (uploadSelectedStations.length > 0) {
          <div class="selected-stations">
            @for (station of uploadSelectedStations; track station.id) {
              <div class="station-chip">
                <span>{{ station.name }} ({{ station.call_sign }})</span>
                <button
                  type="button"
                  class="chip-remove"
                  (click)="removeUploadStation(station.id)"
                  aria-label="Remove station">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
          <p class="selected-count">{{ uploadSelectedStations.length }} station(s) selected</p>
        }
      </div>
    }
  </div>
</ng-template>

<!-- Record tab: coverage cities + station selection -->
<ng-template #recordingDistributionFields>
  <div class="distribution-fields">
    @if (usesCoverageCityFlow) {
      <p class="coverage-helper" role="note">
        <mat-icon aria-hidden="true">info_outline</mat-icon>
        <span>These are the list of cities that currently have active stations. If the city you're looking for is not listed, please select the nearest city and station instead.</span>
      </p>

      <app-coverage-city-select
        [multiple]="true"
        [required]="true"
        [stationCoverageOnly]="true"
        label="Coverage Cities"
        placeholder="Search and select cities"
        [(ngModel)]="selectedCoverageCities"
        (citiesChange)="onCoverageCitiesChange($event)">
      </app-coverage-city-select>

      @if (selectedCoverageCities.length > 0) {
        @if (loadingStations) {
          <div class="inline-loading" role="status">
            <mat-spinner diameter="22"></mat-spinner>
            <span>Loading stations for selected cities…</span>
          </div>
        } @else if (stationsLoadError) {
          <div class="inline-error" role="alert">{{ stationsLoadError }}</div>
        } @else if (availableStations.length === 0) {
          <div class="inline-empty" role="status">No stations found for selected cities.</div>
        } @else {
          <app-station-multiselect-field
            [stations]="availableStations"
            [(selectedStations)]="recordingSelectedStations"
            label="Stations"
            hint="Choose which stations this recording should be submitted to for approval."
            [required]="true" />
        }
      }
    } @else {
      <div class="stations">
        <h4>Select Stations</h4>
        <p class="muted">Choose which stations this recording will be available on</p>

        <div class="multiselect-container">
          <div class="multiselect-input-wrapper">
            <input
              type="text"
              class="multiselect-input"
              placeholder="Search and select stations..."
              [(ngModel)]="recordingStationSearchInput"
              (input)="filterRecordingStations(recordingStationSearchInput)"
              (focus)="recordingStationDropdownOpen = true"
              (blur)="onRecordingStationBlur()">
            <mat-icon class="multiselect-icon">expand_more</mat-icon>
          </div>

          @if (recordingStationDropdownOpen) {
            <div
              class="multiselect-dropdown"
              (mouseenter)="recordingDropdownHover=true"
              (mouseleave)="recordingDropdownHover=false">
              @if (filteredRecordingStations.length > 0) {
                @for (station of filteredRecordingStations; track station.id) {
                  <div class="multiselect-option" (click)="toggleRecordingStation(station)">
                    <input
                      type="checkbox"
                      [checked]="isStationSelected(recordingSelectedStations, station)"
                      (click)="$event.stopPropagation()"
                      (change)="onRecordingCheckboxChange(station, $event)">
                    <span class="option-text">
                      <span class="option-name">{{ station.name }}</span>
                      <span class="option-call-sign">{{ station.call_sign }}</span>
                    </span>
                  </div>
                }
              } @else {
                <div class="multiselect-no-results">No stations found</div>
              }
            </div>
          }
        </div>

        @if (recordingSelectedStations.length > 0) {
          <div class="selected-stations">
            @for (station of recordingSelectedStations; track station.id) {
              <div class="station-chip">
                <span>{{ station.name }} ({{ station.call_sign }})</span>
                <button
                  type="button"
                  class="chip-remove"
                  (click)="removeRecordingStation(station.id)"
                  aria-label="Remove station">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            }
          </div>
          <p class="selected-count">{{ recordingSelectedStations.length }} station(s) selected</p>
        }
      </div>
    }
  </div>
</ng-template>
